import React, { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "../../../components/ui/form";
import Button from "../../../components/common/button";
import { Input } from "../../../components/ui/input";
import toast from "react-hot-toast";
import { useLocation } from "react-router-dom";
import { db } from "../../../config/firebaseConfig";
import { doc, getDoc, updateDoc } from "firebase/firestore";
import { Spinner } from "../../../components/Spinner";

const formSchema = z.object({
  pris: z.string().min(1, "Legg inn indeksert beløp ihht. kontrakt er påkrevd"),
  date: z.string().min(1, "Dato er påkrevd"),
});

export const Indexing: React.FC<{
  Kontraktsum: any;
  setIndexregulering: any;
  setIndexreguleringData: any;
  IndexreguleringData: any;
}> = ({
  Kontraktsum,
  setIndexregulering,
  setIndexreguleringData,
  IndexreguleringData,
}) => {
  const location = useLocation();
  const pathSegments = location.pathname.split("/");
  const id = pathSegments.length > 2 ? pathSegments[2] : null;
  const [loading, setLoading] = useState(false);

  const form = useForm<any>({
    resolver: zodResolver(formSchema),
  });

  const onSubmit = async (data: any) => {
    try {
      setLoading(true);
      setIndexreguleringData(data);

      const formatDate = (date: Date) => {
        return date
          .toLocaleString("sv-SE", { timeZone: "UTC" })
          .replace(",", "");
      };

      const docRef = doc(db, "bank_leads", String(id));

      const existingData = (await getDoc(docRef)).data()?.Fremdriftsplan || {};
      await updateDoc(docRef, {
        Fremdriftsplan: { ...existingData, Indexing: data },
        updatedAt: formatDate(new Date()),
      });
      toast.success("Lagret", { position: "top-right" });
      setIndexregulering(false);
    } catch (error) {
      console.error("Firestore operation failed:", error);
      toast.error("Something went wrong. Please try again.", {
        position: "top-right",
      });
    } finally {
      setLoading(false);
    }
  };

  const prisValue = form.watch("pris");

  const numericKontraktsum =
    Number(String(Kontraktsum ?? "").replace(/\D/g, "")) || 0;
  const numericPris = Number(String(prisValue ?? "").replace(/\D/g, "")) || 0;

  const totalPrice = useMemo(
    () => numericKontraktsum + numericPris,
    [numericKontraktsum, numericPris]
  );

  useEffect(() => {
    if (IndexreguleringData) {
      form.setValue("pris", IndexreguleringData?.pris);
      form.setValue("date", IndexreguleringData?.date);
    }
  }, []);

  return (
    <>
      {loading && <Spinner />}

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="relative">
          <div className="p-5 flex flex-col gap-5">
            <FormField
              control={form.control}
              name={``}
              render={({ field, fieldState }) => (
                <FormItem>
                  <p
                    className={`${
                      fieldState.error ? "text-red" : ""
                    } mb-[6px] text-sm`}
                  >
                    Kontraktsum
                  </p>
                  <FormControl>
                    <Input
                      placeholder="Skriv inn Kontraktsum"
                      {...field}
                      className={`bg-white rounded-[8px] border text-black
                                  ${
                                    fieldState?.error
                                      ? "border-red"
                                      : "border-gray1"
                                  } `}
                      inputMode="numeric"
                      type="text"
                      value={(() => {
                        const raw = Kontraktsum ?? "";
                        if (raw === "") return "";
                        const digits = String(raw).replace(/\D/g, "");
                        if (!digits) return "";
                        const num = Number(digits);
                        return isNaN(num)
                          ? ""
                          : new Intl.NumberFormat("no-NO").format(num);
                      })()}
                      disable={true}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name={`pris`}
              render={({ field, fieldState }) => (
                <FormItem>
                  <p
                    className={`${
                      fieldState.error ? "text-red" : ""
                    } mb-[6px] text-sm`}
                  >
                    Legg inn indeksert beløp ihht. kontrakt
                  </p>
                  <FormControl>
                    <Input
                      placeholder="Skriv inn Legg inn indeksert beløp ihht. kontrakt"
                      {...field}
                      className={`bg-white rounded-[8px] border text-black
                                  ${
                                    fieldState?.error
                                      ? "border-red"
                                      : "border-gray1"
                                  } `}
                      inputMode="numeric"
                      type="text"
                      onChange={({ target: { value } }: any) =>
                        field.onChange({
                          target: {
                            name: `pris`,
                            value: value.replace(/\D/g, "")
                              ? new Intl.NumberFormat("no-NO").format(
                                  Number(value.replace(/\D/g, ""))
                                )
                              : "",
                          },
                        })
                      }
                      value={field.value === null ? "-" : field.value}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name={`date`}
              render={({ field, fieldState }) => (
                <FormItem>
                  <p
                    className={`${
                      fieldState.error ? "text-red" : ""
                    } mb-[6px] text-sm`}
                  >
                    Dato
                  </p>
                  <FormControl>
                    <Input
                      placeholder="Skriv inn dato"
                      {...field}
                      className={`bg-white rounded-[8px] border text-black
                                          ${
                                            fieldState?.error
                                              ? "border-red"
                                              : "border-gray1"
                                          } `}
                      type="date"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name={``}
              render={({ field, fieldState }) => (
                <FormItem>
                  <p
                    className={`${
                      fieldState.error ? "text-red" : ""
                    } mb-[6px] text-sm`}
                  >
                    Ny totalsum
                  </p>
                  <FormControl>
                    <Input
                      placeholder="Skriv inn Ny totalsum"
                      {...field}
                      className={`bg-white rounded-[8px] border text-black
                                  ${
                                    fieldState?.error
                                      ? "border-red"
                                      : "border-gray1"
                                  } `}
                      inputMode="numeric"
                      type="text"
                      value={
                        totalPrice
                          ? new Intl.NumberFormat("no-NO").format(totalPrice)
                          : ""
                      }
                      disable={true}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <div className="flex justify-end w-full gap-5 items-center px-5 pb-10 left-0">
            <div onClick={() => setIndexregulering(false)}>
              <Button
                text="Tilbake"
                className="border border-gray2 text-black text-sm rounded-[8px] h-[40px] font-medium relative px-4 py-[10px] flex items-center gap-2"
              />
            </div>
            <div id="submit">
              <Button
                text="Neste"
                className="border border-primary bg-primary text-white text-sm rounded-[8px] h-[40px] font-medium relative px-4 py-[10px] flex items-center gap-2"
                type="submit"
              />
            </div>
          </div>
        </form>
      </Form>
    </>
  );
};
